/* eslint-disable @atlaskit/design-system/use-tokens-space */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
// eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
import { css, jsx, keyframes } from '@compiled/react';

import Button from '@atlaskit/button/default/button';
import { token } from '@atlaskit/tokens';
import { Text } from '@atlaskit/primitives/compiled/text';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { LazyAnimatedRovoIcon } from '@atlassian/conversation-assistant-ui-components';

import { usePageContentId } from '@confluence/page-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import { getRendererAnnotationEventEmitter } from '@confluence/annotation-event-emitter/entry-points/getRendererAnnotationEventEmitter.ts';
import { AnnotationMarkStates, AnnotationTypes } from '@atlaskit/adf-schema/annotation';
import { AnnotationUpdateEvent } from '@atlaskit/editor-common/types/emitter';

import { useSuggestEdit, getCommentTextFromAdf } from './hooks/useSuggestEdit.ts';

const i18n = defineMessages({
	suggestEdit: {
		id: 'ai-suggest-edit.button.label',
		defaultMessage: 'Suggest Edit',
		description: 'Button label to suggest an edit to the annotated content using Rovo AI',
	},
	loading: {
		id: 'ai-suggest-edit.button.loading',
		defaultMessage: 'Suggesting edit...',
		description: 'Button label shown while the suggest edit request is being sent',
	},
	error: {
		id: 'ai-suggest-edit.button.error',
		defaultMessage: 'Retry suggest edit',
		description: 'Button label shown when the suggest edit request fails',
	},
	rovoLoadingAlt: {
		id: 'ai-suggest-edit.rovo-loading.alt',
		defaultMessage: 'Rovo is processing',
		description:
			'The text is the alt text for the Rovo AI loading animation icon, shown while the suggest edit request is being processed.',
	},
});

// Rainbow border animation keyframes
const animateBorder = keyframes({
	from: {
		'--angle': '0deg',
	},
	to: {
		'--angle': '360deg',
	},
});

const buttonContainerStyle = css({
	paddingTop: token('space.100'),
	paddingBottom: token('space.050'),
});

// Rainbow gradient border styles for loading state
const rainbowBorderStyle = css({
	'--angle': '0deg',
	position: 'relative',
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors
	'&::before': {
		content: '""',
		position: 'absolute',
		inset: '-2px',
		// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
		borderRadius: token('radius.small', '4px'),
		padding: '2px',
		background: `conic-gradient(from var(--angle), ${token('color.border.brand')}, ${token('color.border.brand')}, ${token('color.border.discovery')}, ${token('color.border.warning')}, ${token('color.border.brand')})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
		animation: `1s ${animateBorder} linear infinite`,
		WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
		WebkitMaskComposite: 'xor',
		maskComposite: 'exclude',
		pointerEvents: 'none',
	},
});

// CSS property declaration for --angle (needed for the animation)
const anglePropertyStyle = css({
	'@property --angle': {
		syntax: "'<angle>'",
		initialValue: '0deg',
		inherits: 'false',
	},
});

const loadingIconStyle = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '16px',
	height: '16px',
});

// Style to make the wrapper only as wide as its content
const fitContentStyle = css({
	width: 'fit-content',
});

type SuggestEditIconProps = {
	isLoading: boolean;
	error: string | null;
	loadingAlt: string;
};

const SuggestEditIcon = ({ isLoading, error, loadingAlt }: SuggestEditIconProps) => {
	if (isLoading) {
		return (
			<span css={loadingIconStyle}>
				<LazyAnimatedRovoIcon alt={loadingAlt} width="16px" height="16px" />
			</span>
		);
	}
	if (error) {
		// Icon is decorative - button text provides accessible name
		return <CrossCircleIcon label="" color={token('color.icon.danger')} />;
	}
	// Icon is decorative - button text provides accessible name
	return <RovoIcon label="" />;
};

export type AiSuggestEditProps = {
	/**
	 * The annotation ID associated with the inline comment
	 */
	annotationId?: string;
	/**
	 * The text content that is annotated (highlighted) in the page
	 */
	annotatedText?: string;
	/**
	 * The ADF content of the comment body
	 */
	commentAdf?: string;
	/**
	 * The comment ID
	 */
	commentId: string;
	/**
	 * The content ID of the page
	 */
	contentId?: string;
};

export const AiSuggestEdit = ({
	annotationId,
	annotatedText,
	commentAdf,
	commentId: _commentId,
	contentId: contentIdProp,
}: AiSuggestEditProps): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { isRovoEnabled } = useSessionData();
	const pageMode = useGetPageMode();

	const [pageContentId] = usePageContentId();

	const contentId = pageContentId || contentIdProp;

	// IMPORTANT: keep feature gate / experiment usage inline so it remains statically analyzable.

	const { suggestEdit, isLoading, error, isSuccess, reset } = useSuggestEdit({
		contentId,
		enabled: isRovoEnabled && fg('aifc_create_enabled'),
	});

	// Handle successful edit: resolve the inline comment
	useEffect(() => {
		if (isSuccess) {
			if (annotationId) {
				const eventEmitter = getRendererAnnotationEventEmitter();
				if (pageMode === PageMode.VIEW) {
					eventEmitter.emit(AnnotationUpdateEvent.SET_ANNOTATION_STATE, {
						[annotationId]: {
							id: annotationId,
							annotationType: AnnotationTypes.INLINE_COMMENT,
							state: AnnotationMarkStates.RESOLVED,
						},
					});
				} else {
					eventEmitter.emit('resolve', annotationId);
				}
			}
		}
	}, [isSuccess, annotationId, pageMode]);

	const handleClick = useCallback(() => {
		if (!fg('aifc_create_enabled')) {
			return;
		}

		if (isLoading) {
			reset();
			return;
		}

		const commentText = getCommentTextFromAdf(commentAdf);

		suggestEdit({
			annotatedText,
			commentText,
			contentId,
		});
	}, [annotatedText, commentAdf, contentId, isLoading, reset, suggestEdit]);

	const loadingAlt = formatMessage(i18n.rovoLoadingAlt);
	const renderIcon = useCallback(
		() => <SuggestEditIcon isLoading={isLoading} error={error} loadingAlt={loadingAlt} />,
		[isLoading, error, loadingAlt],
	);

	const isAllowedPageMode = pageMode === PageMode.LIVE || pageMode === PageMode.EDIT;

	if (
		!annotationId ||
		!isAllowedPageMode ||
		!isRovoEnabled ||
		!fg('aifc_create_enabled') ||
		!expValEquals('ai-suggested-edits', 'isEnabled', true)
	) {
		return null;
	}

	return (
		<div css={buttonContainerStyle} aria-live="polite">
			<div
				css={[
					isLoading && rainbowBorderStyle,
					isLoading && anglePropertyStyle,
					isLoading && fitContentStyle,
				]}
			>
				<Button
					appearance="subtle"
					iconBefore={renderIcon}
					onClick={handleClick}
					testId="ai-suggest-edit-button"
				>
					{error && (
						<Text as="span" color="color.text.danger">
							{formatMessage(i18n.error)}
						</Text>
					)}
					{!error && (isLoading ? formatMessage(i18n.loading) : formatMessage(i18n.suggestEdit))}
				</Button>
			</div>
		</div>
	);
};
